import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STAGE_DOTS, STAGE_ORDER, displayName, statusLabel } from "./shared";

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `há ${Math.max(mins, 1)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "ontem" : `há ${days} dias`;
};

export const PipelineView = ({ leads, onOpen, onChanged }) => {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const serviceLeads = useMemo(() => leads.filter((l) => l.type === "service"), [leads]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGE_ORDER.map((s) => [s, []]));
    for (const lead of serviceLeads) {
      if (!STAGE_ORDER.includes(lead.status)) continue;
      map[lead.status].push(lead);
    }
    return map;
  }, [serviceLeads]);

  const onDragStart = (e, lead) => {
    e.dataTransfer.setData("text/plain", lead.id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(lead.id);
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOverStage(null);
    setDraggingId(null);
    const id = e.dataTransfer.getData("text/plain");
    const lead = serviceLeads.find((l) => l.id === id);
    if (!lead || lead.status === stage) return;
    try {
      await api.patch(`/admin/leads/${id}`, { status: stage });
      toast.success(`${displayName(lead)} → ${statusLabel(stage)}`);
      onChanged();
    } catch {
      toast.error("Falha ao mover o card.");
    }
  };

  return (
    <div data-testid="pipeline-view">
      <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">Pipeline</h1>
      <p className="mt-1 text-sm text-slate-500">
        Arraste os cards entre as colunas para mudar a etapa — ou clique para abrir o detalhe.
      </p>

      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-petrol-800 shadow-[0_40px_80px_-30px_rgba(11,25,44,0.5)]">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>
          <div className="flex-1 text-center">
            <span className="rounded-md bg-white/5 px-4 py-1 font-mono text-[0.65rem] tracking-widest text-white/50">
              painel resolv — pipeline
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-widest text-emerald-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            ao vivo
          </span>
        </div>

        <div className="overflow-x-auto p-4">
          <div className="flex min-w-[1560px] gap-3">
            {STAGE_ORDER.map((stage) => {
              const color = STAGE_DOTS[stage];
              const items = byStage[stage];
              const isTarget = dragOverStage === stage && draggingId;
              return (
                <div
                  key={stage}
                  data-testid={`pipeline-col-${stage}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverStage !== stage) setDragOverStage(stage);
                  }}
                  onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
                  onDrop={(e) => onDrop(e, stage)}
                  className={`flex-1 rounded-xl p-3 transition-[background-color,box-shadow] duration-200 ${
                    isTarget
                      ? "bg-white/[0.09] shadow-[inset_0_0_0_2px_rgba(255,92,0,0.6)]"
                      : "bg-white/[0.04]"
                  }`}
                >
                  <div className="mb-3 flex items-center justify-between px-1">
                    <span className="flex items-center gap-2 text-[0.7rem] font-semibold text-white/80">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <span className="leading-tight">{statusLabel(stage)}</span>
                    </span>
                    <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono text-[0.65rem] text-white/70">
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {items.map((lead) => (
                      <button
                        key={lead.id}
                        draggable
                        onDragStart={(e) => onDragStart(e, lead)}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOverStage(null);
                        }}
                        onClick={() => onOpen(lead)}
                        data-testid={`pipeline-card-${lead.id}`}
                        className={`w-full cursor-grab rounded-lg border border-white/[0.07] bg-petrol p-3 text-left transition-[border-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:border-brand/60 active:cursor-grabbing ${
                          lead.payload?.priority === "Urgente" ? "border-l-2 border-l-red-500" : ""
                        } ${draggingId === lead.id ? "opacity-40" : ""}`}
                      >
                        <p className="truncate text-xs font-semibold leading-snug text-white">
                          {displayName(lead)}
                        </p>
                        <p className="mt-0.5 truncate text-[0.65rem] text-white/40">
                          {lead.payload?.city || "—"}
                        </p>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span
                            className="max-w-[70%] truncate rounded-full px-2 py-0.5 text-[0.6rem] font-semibold"
                            style={{ backgroundColor: `${color}22`, color }}
                          >
                            {lead.payload?.service_type || "a classificar"}
                          </span>
                          <span className="shrink-0 font-mono text-[0.6rem] text-white/40">
                            {timeAgo(lead.created_at)}
                          </span>
                        </div>
                      </button>
                    ))}
                    {items.length === 0 && (
                      <div className="rounded-lg border border-dashed border-white/10 p-2.5 text-center font-mono text-[0.6rem] uppercase tracking-widest text-white/25">
                        {isTarget ? "solte aqui" : "vazio"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
